import {
  isBrowserLocationChangeRequired,
  isUiNodeInputAttributes,
  ResponseError,
  UiNodeAttributes,
} from "@ory/client-fetch";
import createOryClient from "./createOryClient";

type InputAttributes = Extract<UiNodeAttributes, { node_type: "input" }>;

type Options = {
  // Kratos public API origin
  oryOrigin: string;
  // Kratos OIDC provider id
  provider: string;
  // must match Kratos's allowed_return_urls
  returnTo: string;
};

// Starts a Kratos registration flow for an OIDC provider and returns the URL
// the browser must visit to sign in with it. Throws if Kratos doesn't ask for
// a redirect.
export default async function startSsoRegistration({
  oryOrigin,
  provider,
  returnTo,
}: Options): Promise<string> {
  const oryClient = createOryClient(oryOrigin);
  if (!oryClient) throw new Error("Ory origin is not defined");

  try {
    // Registration flows match by credential before creating a new
    // identity, so an existing (or newly-linked) account logs in
    // transparently too - this one flow handles both registration and
    // login.
    // Without Accept: application/json Kratos treats this as a browser
    // navigation and 303s to the Kratos UI, which fetch follows
    // cross-origin and fails on CORS.
    const flow = await oryClient.createBrowserRegistrationFlow(
      { returnTo },
      { headers: { Accept: "application/json" } }
    );

    const csrfToken = flow.ui.nodes
      .map((node) => node.attributes)
      .find(
        (attrs): attrs is InputAttributes =>
          isUiNodeInputAttributes(attrs) && attrs.name === "csrf_token"
      )?.value as string | undefined;

    await oryClient.updateRegistrationFlow({
      flow: flow.id,
      updateRegistrationFlowBody: {
        method: "oidc",
        provider,
        csrf_token: csrfToken,
      },
    });
  } catch (err) {
    // Kratos always answers a successful oidc method submission with a
    // browser redirect, which the client surfaces as an error.
    if (err instanceof ResponseError) {
      const body: unknown = await err.response
        .clone()
        .json()
        .catch(() => null);
      if (isBrowserLocationChangeRequired(body) && body.redirect_browser_to) {
        return body.redirect_browser_to;
      }
    }
    throw err;
  }

  throw new Error(`Kratos did not redirect for provider ${provider}`);
}
